import { h, Fragment } from 'preact';
import { forwardRef } from 'preact/compat';
import { formatMoney } from 'utils/storefront/format-resource';
import { useEffect, useMemo, useState } from 'preact/hooks';
import Layout from 'utils/layout';
import useInView from 'utils/in-view';
import VariantSelector from './variant-selector';
import ProductStickyButton from './product-sticky-button';
import ProductQuantity from './product-quantity';
import extractAndFormat from '../utils/extract-and-format';
import { useCart } from '../cart/cart-context';

const AddToCartButton = ({
  product,
  currentVariant,
  isQuickView,
  qty,
  subscribeButton,
  sellingPrice,
  kitSet,
  url,
  alreadyAddedQty,
  cart
}) => {

  const [limitExceeded, setLimitExceeded] = useState(false);
  useEffect(() => {
    const variantQtyLimit = Number(
      currentVariant.metafields?.cartValidation?.quantityLimit,
    );
    const productQtyLimit = Number(
      currentVariant.productMetafields?.cartValidation?.quantityLimit,
    );
    const minValue =
      variantQtyLimit && productQtyLimit
        ? Math.min(variantQtyLimit, productQtyLimit)
        : variantQtyLimit || productQtyLimit;
    if (minValue > 0) {
      const currentVariantId = Number(currentVariant.id.split('/').pop());
      const matchedLineItem = cart?.state?.items?.find(
        (item) => item.id === currentVariantId,
      );
      const alreadyAddedQty = matchedLineItem ? matchedLineItem.quantity : 0;
      const quantitySum = qty + alreadyAddedQty;
      if (quantitySum > minValue) {
        setLimitExceeded(true);
      } else {
        setLimitExceeded(false);
      }
    }
  }, [currentVariant, qty, cart]);

  const saveRechargeDataToStore = () => {
    const id = currentVariant.id.split('/ProductVariant/')[1];
    console.log('PRODUCT FORM', product);
    const allSellingPlans = product.selling_plan_groups.filter(spg => spg.app_id === 'SKIO').flatMap(spg => spg.selling_plans);
    const variantSellingPlans = allSellingPlans.filter(sp => currentVariantJson.sellingPlanIds.includes(sp.id));
    if (!variantSellingPlans.length) return

    const rechargeArrayObjects = [...variantSellingPlans].map(input => {
      return {
        value: input.id,
        text: input.name
      }
    })
    const obj = { ...getRechargeDataFromStorage() }

    obj[id] = {
      ...rechargeArrayObjects
    }

    setRechargeDataToStorage(obj);
  }


  const buttonClasses = isQuickView
    ? 'h-10 items-center py-3 px-3 label-10 uppercase flex justify-between w-full border border-supergoop-blue text-supergoop-blue mt-auto'
    : 'button button-primary uppercase label-12 tracking-[2px] w-full p-0';
  const handleAddToCart = async (e) => {
    e.preventDefault();
    saveRechargeDataToStore();

    const attributes = [];

    const $skioPlanPicker  = document.querySelector('skio-plan-picker');
    const selling_plan = $skioPlanPicker?.selectedSellingPlan?.id;    

    const props = {
      detail: {
        payload: {
          merchandiseId: currentVariant.id,
          selling_plan,
          attributes,
          quantity: qty,
        },
      }
    };

    if (currentVariant.variantBundle) {
      const bundleDefaultVariantId = currentVariant.variantBundle.variants[0].id;
      props.detail.payload.merchandiseId = `gid://shopify/ProductVariant/${bundleDefaultVariantId}`
    }

    const addToCartEvent = new CustomEvent('cart:add', props);
    await document.dispatchEvent(addToCartEvent);
    await cart.get()
    if (currentVariant.quantityAvailable == alreadyAddedQty) {
      const currentVariantId = Number(currentVariant.id.split('/').pop());
      const currentCartItemToggle = document.querySelector(`#line-${currentVariantId} .toggle-error`)
      currentCartItemToggle.click();
    }
  };

  return (
    <>
      {kitSet ? (
        <a
          className="h-10 no-underline items-center py-3 px-3 label-10 uppercase flex justify-center w-full border border-grey-2A text-grey-2A focus:border-supergoop-blue peer-[.test]:border-supergoop-blue focus:text-supergoop-blue lg:border-supergoop-blue lg:text-supergoop-blue mt-auto"
          disabled={!currentVariant.availableForSale || limitExceeded}
          href={url}
        >
          <span>
            {currentVariant
              ? window.TMW.Strings.product.shopNow
              : window.TMW.Strings.product.unavailable}
          </span>
        </a>
      ) : (
        <button
          type="submit"
          className={buttonClasses}
          disabled={!currentVariant.availableForSale || limitExceeded}
          onClick={(e) => handleAddToCart(e)}
        >
          <span>
            {currentVariant
              ? currentVariant?.availableForSale
                ? window.TMW.Strings.product.addToCart
                : window.TMW.Strings.product.outOfStock
              : window.TMW.Strings.product.unavailable}
          </span>
          <span>
            {!isQuickView && ' - '}{' '}
            {subscribeButton ?
              sellingPrice.isPercentage ?
                extractAndFormat(formatMoney({ amount: (currentVariant.price.amount - currentVariant.price.amount * sellingPrice.value) * qty })) :
                extractAndFormat(formatMoney({ amount: (currentVariant.price.amount + sellingPrice.value) * qty })) :
              extractAndFormat(formatMoney({ amount: currentVariant.price.amount * qty }),
              )}
          </span>
        </button>
      )}
    </>
  );
};

export const getRechargeDataFromStorage = () => {
  try {
    return JSON.parse(localStorage.getItem('rechargeElements'))
  }
  catch (e) {
    console.error(e);
  }
}

export const setRechargeDataToStorage = (data) => {
  localStorage.setItem('rechargeElements', JSON.stringify(data))
}

const ProductForm = forwardRef((props, ref) => {
  const {
    kitSet,
    url,
    galleryInView,
    product,
    showStickyAtc,
    showFindationWidget,
    findationApiKey,
    findationSetId,
    isQuickView,
    currentVariant,
    setCurrentVariant
    
  } = props;

  const { cart } = useCart();
  const [formContainer, formInView] = useInView();
  const [qty, setQty] = useState(1);
  const [subscribeButton, setSubscribe] = useState(false);
  const [sellingPrice, setSellingPrice] = useState({
    isPercentage: false,
    value: 0,
  });
  // Lookup variant based on selected options
  const isStickyVisible = useMemo(() => {
    const layout = Layout.getInstance();
    if (layout.isBelowBreakpoint('lg')) {
      return !formInView && !galleryInView;
    }
    return !formInView;
  }, [formInView, galleryInView]);


  const alreadyAddedQty = useMemo(() => {
    const currentVariantId = Number(currentVariant.id.split('/').pop());
    const matchedLineItem = cart?.state?.items?.find(
      (item) => item.id === currentVariantId,
    );
    return matchedLineItem ? matchedLineItem.quantity : 0;
  }, [currentVariant, cart, cart?.state?.items])

  const colorSelector = useMemo(
    () =>
      props.product.options.map((option) => ((option.name.toLowerCase().includes('color') && <VariantSelector
        currentVariant={currentVariant}
        key={option.name}
        option={option}
        setCurrentVariant={setCurrentVariant}
        variants={props.product.variants}
        product={props.product}
        isQuickView={isQuickView}
      />)
      )),
    [currentVariant],
  );
  const variantSelectors = useMemo(
    () =>
      !props.product.hasOnlyDefaultVariant &&
      props.product.options.map((option) => (
        (!option.name.toLowerCase().includes('color') && <VariantSelector
          currentVariant={currentVariant}
          key={option.name}
          option={option}
          setCurrentVariant={setCurrentVariant}
          variants={props.product.variants}
          product={props.product}
          isQuickView={isQuickView}
        />)
      )),
    [currentVariant],
  );
  const addToCartButton = useMemo(
    () => (
      <AddToCartButton
        product={product}
        subscribeButton={subscribeButton}
        currentVariant={currentVariant}
        isQuickView={isQuickView}
        qty={qty}
        alreadyAddedQty={alreadyAddedQty}
        kitSet={kitSet}
        url={url}
        cart={cart}
        sellingPrice={sellingPrice}
      />
    ),
    [currentVariant, qty, cart, alreadyAddedQty, subscribeButton, sellingPrice],
  );

  const productQuantity = useMemo(
    () => (
      <ProductQuantity
        quantity={qty}
        alreadyAddedQty={alreadyAddedQty}
        changeQuantity={(value) => {
          setQty(value);
        }}
        cart={cart}
        currentVariant={currentVariant}
        maxQty={currentVariant.quantityAvailable}
      />
    ),
    [currentVariant, product, qty, alreadyAddedQty],
  );

  useEffect(() => {
    if (window.location && window.location.toString().includes('products/')) {
      const searchParams = new URLSearchParams(window.location.search);
      const id = currentVariant.id.split('/ProductVariant/')[1];
      searchParams.set('variant', id);
      const newRelativePathQuery = `${window.location.pathname
        }?${searchParams.toString()}`;
      window.history.pushState(null, '', newRelativePathQuery);
    }
  }, [currentVariant]);

  const [widgetContainer, updateWidgetContainer] = useState(false);

  let informationBlock = '<div id="yotpo-subscriptions-info-block-container" class="yotpo-subscriptions-info-block-container"><div class="benefits-text">10% off + free shipping on every delivery</div><div class="terms-and-conditions-text"><div>Adjust order intervals, skip orders or pause. Cancel anytime.</div><a href="https://supergoop.com/pages/supermail" target="_blank" class="terms-and-conditions-link">Auto-Replenish Terms</a></div><div class="discount-codes-text">Discount codes not applicable to auto-replenish unless otherwise specified.</div></div>'
  let [sellingPlansExists, setExistSellingPlans] = useState(false);

  useEffect(() => {
    for (const [key] of Object.entries(product?.sellingPlans)) {
      product?.sellingPlans[key].value && setExistSellingPlans(true)
    }
  }, [product, product.sellingPlans])


  useEffect(() => {
    if (!showFindationWidget) return;

    const initWidget = () => {
      window.Findation = window.Findation || {};
      Findation.init(
        document.getElementById("findation-widget-button"),
        findationApiKey, // Template literals not needed if it's already a string
        {
          productId: product.id.split('/').pop(),
          searchSetLookupReference: findationSetId // Template literals not needed if it's already a string
        }
      );
    };

    // Function to check for Findation and initialize widget
    const attemptInit = () => {
      if (window.Findation) {
        initWidget();
        return true; // Indicates success
      }
      return false; // Indicates failure
    };

    // Try to initialize immediately in case Findation is already loaded
    if (!attemptInit()) {
      // Only set up the interval if immediate initialization fails
      const interval = setInterval(() => {
        if (attemptInit()) {
          clearInterval(interval);
        }
      }, 100); // Added interval delay, assuming it was accidentally omitted
    }

    // Cleanup to ensure the interval is cleared if the component unmounts before initialization
    return () => clearInterval(interval);
  }, [showFindationWidget, findationApiKey, findationSetId, product.id]);

  return (
    <>
      <div ref={formContainer}>
        <form
          method="post"
          action="/cart/add"
          acceptCharset="UTF-8"
          encType="multipart/form-data"
          noValidate="novalidate"
          data-product-form=""
          data-has-only-default-variant={
            props.product.variants.length > 1 ? true : ''
          }
          className={`flex flex-col ${props.product.variants.length > 1 && ' '}  ${!!isQuickView && 'lg:hidden'
            } w-full bg-white top-full z-10`}
        >
          <div ref={ref} className='-order-2'>
            {colorSelector}
            {showFindationWidget && <script type="text/javascript" src="https://assets.findation.com/javascripts/w-adv-12.min.js"></script>}
            {showFindationWidget && <div id="findation-widget-button"></div>}
            {variantSelectors}
          </div>

          {
            isQuickView ? (
              addToCartButton
            ) : (
              <div id="atc-wrapper" className="flex">
                {addToCartButton}
                {productQuantity}
              </div>
            )
          }
          <div id="attn-waitlist-trigger"></div>

          <input
            type="hidden"
            name="id"
            value={currentVariant.id.split('/ProductVariant/')[1]}
          />
        </form>
      </div>
      {showStickyAtc && (
        <ProductStickyButton
          product={product}
          variant={currentVariant}
          options={product.options}
          isVisible={isStickyVisible}
        >
          {addToCartButton}
        </ProductStickyButton>
      )}
    </>
  );
});

export default ProductForm;
